import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, CalendarDays, BellRing, Store, BadgeCheck, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { useLang } from "@/i18n";
import { AdeCoinIcon } from "@/components/AdeCoinIcon";

const fmtDist = (m) => (m == null ? null : m < 1000 ? `${Math.round(m / 50) * 50} m` : `${(m / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })} km`);

const fmtWindow = (mission, lang) => {
  if (!mission.starts_at) return null;
  const d = new Date(mission.starts_at);
  const day = d.toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "long" }).toUpperCase();
  const time = mission.daily_start_time && mission.daily_end_time
    ? `${mission.daily_start_time}–${mission.daily_end_time}`
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${time}`;
};

export const MissionCard = ({ mission, onInterestChange }) => {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [interested, setInterested] = useState(!!mission.my_interest);
  const [busy, setBusy] = useState(false);
  const dwellMin = Math.round((mission.minimum_dwell_seconds || 900) / 60);
  const dist = fmtDist(mission.distance_meters);
  const storeName = mission.store?.name || mission.title;
  const heroImg = mission.store?.image_url;

  const toggleInterest = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (interested) {
        await api.delete(`/missions/${mission.campaign_id}/interest`);
        setInterested(false);
      } else {
        await api.post(`/missions/${mission.campaign_id}/interest`);
        setInterested(true);
        toast.success(t("missionWillNotify"));
      }
      onInterestChange?.(mission.campaign_id, !interested);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("missionErrorGeneric"));
    } finally {
      setBusy(false);
    }
  };

  const open = (e) => { e?.stopPropagation(); navigate(`/app/missions/${mission.campaign_id}`); };

  return (
    <div className="mission-feed-card mission-rise mx-3 overflow-hidden cursor-pointer" onClick={open}
         data-testid={`mission-card-${mission.campaign_id}`}>
      {/* Hero foto locale */}
      <div className="relative">
        {heroImg ? (
          <img src={heroImg} alt={storeName} className="w-full h-48 object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-24" style={{ background: "linear-gradient(120deg,#2c1b4d,#4a1d5e,#1b2450)" }} />
        )}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0d0a18] to-transparent" />
        <span className="mission-pill absolute left-4 bottom-3" data-testid={mission.is_scheduled ? "mission-scheduled-badge" : "mission-sponsored-badge"}>
          {t("missionLabel")} · {mission.is_scheduled ? t("missionScheduled") : t("missionSponsored")}
        </span>
      </div>

      <div className="p-5 pt-3">
        {/* Logo + nome */}
        <div className="flex items-center gap-3.5 mb-1.5">
          <div className="mission-logo-ring w-14 h-14 rounded-full flex items-center justify-center shrink-0">
            <Store className="w-6 h-6 text-white/90" />
          </div>
          <div className="min-w-0">
            <p className="text-[22px] font-extrabold text-white leading-tight flex items-center gap-2">
              <span className="truncate">{storeName}</span>
              <BadgeCheck className="text-violet-400 shrink-0" style={{ width: 19, height: 19 }} />
            </p>
            <p className="text-[14px] text-white/65 leading-snug">
              {mission.is_scheduled ? mission.title
                : `${t("missionVisitStore")} ${lang === "it" ? "e rimani almeno" : "and stay at least"} ${dwellMin} ${t("missionMinutes")}`}
            </p>
          </div>
        </div>

        {mission.is_scheduled && (
          <div className="flex items-center gap-1.5 mt-2 text-violet-300 text-sm font-bold" data-testid="mission-schedule-window">
            <CalendarDays className="w-4 h-4" /> {fmtWindow(mission, lang)}
          </div>
        )}

        {/* Reward */}
        <div className="flex items-center gap-3 mt-3 mb-1">
          <span className="mission-coin-pulse inline-flex"><AdeCoinIcon size={40} /></span>
          <span className="mission-reward-text text-[2.4rem] font-extrabold leading-none">
            +{(mission.reward_coins || 0).toLocaleString("it-IT")}
          </span>
          <span className="text-xl font-extrabold text-white">{t("missionCoinsName")}</span>
        </div>

        {!mission.is_scheduled && dist && (
          <p className="flex items-center gap-1.5 text-[15px] text-white/70 mb-4">
            <MapPin className="w-4.5 h-4.5 text-pink-400" style={{ width: 18, height: 18 }} /> {dist} {t("missionFromYou")}
          </p>
        )}
        {(mission.is_scheduled || !dist) && <div className="mb-4" />}

        {/* CTA */}
        {mission.is_scheduled ? (
          interested ? (
            <button onClick={toggleInterest} data-testid="mission-interested-active-btn"
              className="mission-cta-outline w-full py-3.5 rounded-full text-sm font-extrabold text-emerald-300">
              <BellRing className="w-4 h-4 inline mr-1.5 -mt-0.5" />{t("missionWillNotify")}
            </button>
          ) : (
            <button onClick={toggleInterest} data-testid="mission-interested-btn"
              className="mission-cta-hero w-full py-3.5 rounded-full text-[15px] font-extrabold text-white tracking-wide">
              {t("missionInterested")}
            </button>
          )
        ) : (
          <button onClick={open} data-testid="mission-discover-btn"
            className="mission-cta-hero w-full py-3.5 rounded-full text-[15px] font-extrabold text-white tracking-widest flex items-center justify-center gap-1.5">
            {t("missionAccept")} <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};
